const SITE_ORIGIN = "https://orrith.me";
const OG_IMAGE = `${SITE_ORIGIN}/logo.png`;
const TWITTER_HANDLE = "@OrrithHQ";

function normalizePathname(pathname) {
  if (!pathname || pathname === "/") return "/";
  return pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
}

function setHeadMeta(selector, attributes) {
  const head = document.head;
  if (!head) return;

  let node = head.querySelector(selector);
  if (!node) {
    node = document.createElement(attributes.tagName || "meta");
    head.appendChild(node);
  }

  Object.entries(attributes).forEach(([key, value]) => {
    if (key !== "tagName") {
      node.setAttribute(key, value);
    }
  });
}

function setLinkTag(rel, href, extra = {}) {
  const head = document.head;
  if (!head) return;

  let node = head.querySelector(`link[rel="${rel}"]`);
  if (!node) {
    node = document.createElement("link");
    node.setAttribute("rel", rel);
    head.appendChild(node);
  }

  node.setAttribute("href", href);
  Object.entries(extra).forEach(([key, value]) => {
    node.setAttribute(key, value);
  });
}

function getRouteSeo(pathname) {
  const path = normalizePathname(pathname);

  const publicRoutes = {
    "/": {
      title: "Orrith | Operating Infrastructure for Global Agriculture, Food system & Climate Intelligence",
      description:
        "Orrith is building the operating infrastructure for global agriculture — connecting farmers, markets, food logistics, and climate intelligence through trusted networks that improve food access, reduce waste, and create measurable environmental impact.",
      canonical: `${SITE_ORIGIN}/`,
    },
    "/orrith-landing": {
      title: "Orrith | Operating Infrastructure for Global Agriculture, Food Systems & Climate Intelligence",
      description:
        "Orrith is building the operating infrastructure for global agriculture — connecting farmers, markets, food logistics, and climate intelligence through trusted networks that improve food access, reduce waste, and create measurable environmental impact.",
      canonical: `${SITE_ORIGIN}/`,
      ogImage: OG_IMAGE,
    },
    "/gaia-core-landing": {
      title: "Orrith | Operating Infrastructure for Global Agriculture, Food Systems & Climate Intelligence",
      description:
        "Orrith is building the intelligence infrastructure for global agriculture — a data-driven operating layer connecting farmers, markets, food logistics, environmental systems, and communities through trusted networks and measurable impact.",
      canonical: `${SITE_ORIGIN}/`,
      ogImage: OG_IMAGE,
    },
"/our-platform": {
      title: "Our Platform | Orrith",
      description:
        "Explore how Orrith connects Farm, FarmPulse, and FarmMark into an agricultural operating infrastructure platform for farmers, markets, food logistics, and climate intelligence.",
      canonical: `${SITE_ORIGIN}/our-platform`,
      ogImage: OG_IMAGE,
    },
    "/farm": {
      title: "Farm Marketplace | Orrith",
      description:
        "Browse, list, and exchange agricultural produce through Orrith's direct marketplace.",
      canonical: `${SITE_ORIGIN}/farm`,
      ogImage: OG_IMAGE,
    },
    "/farmmark": {
      title: "FarmMark | Environmental Impact & Climate Action | Orrith",
      description:
        "Track carbon savings, view the leaderboard, and measure your climate impact with FarmMark – Orrith's environmental gamification layer for agricultural operating infrastructure.",
      canonical: `${SITE_ORIGIN}/farmmark`,
      ogImage: OG_IMAGE,
    },
    "/farmpulse": {
      title: "FarmPulse | Orrith",
      description:
        "Review agricultural market signals, price trends, and forecasting insights for better decisions.",
      canonical: `${SITE_ORIGIN}/farmpulse`,
      ogImage: OG_IMAGE,
    },
    "/north-star": {
      title: "NorthStar | Orrith",
      description:
        "Explore the NorthStar experience for guided access to Orrith services and platform entry points.",
      canonical: `${SITE_ORIGIN}/north-star`,
      ogImage: OG_IMAGE,
    },
    "/privacy": {
      title: "Privacy Policy | Orrith",
      description:
        "Read Orrith's privacy policy and platform data handling practices.",
      canonical: `${SITE_ORIGIN}/legal/privacy`,
      robots: "noindex, nofollow, noarchive",
    },
    "/terms": {
      title: "Terms of Service | Orrith",
      description:
        "Read Orrith's terms of service and platform usage requirements.",
      canonical: `${SITE_ORIGIN}/legal/terms`,
      robots: "noindex, nofollow, noarchive",
    },
    "/support": {
      title: "Help Center | Orrith",
      description:
        "Find guides, FAQs, support tickets, and contact options for Orrith users.",
      canonical: `${SITE_ORIGIN}/support`,
      ogImage: OG_IMAGE,
    },
    "/accessibility": {
      title: "Accessibility Statement | Orrith",
      description:
        "Read Orrith's accessibility statement and support options for inclusive platform use.",
      canonical: `${SITE_ORIGIN}/accessibility`,
      ogImage: OG_IMAGE,
    },
    "/careers": {
      title: "Careers | Orrith",
      description:
        "See current opportunities and learn how to join the Orrith team.",
      canonical: `${SITE_ORIGIN}/careers`,
      ogImage: OG_IMAGE,
    },
  };

  if (publicRoutes[path]) return publicRoutes[path];

  if (path.startsWith("/legal/")) {
    const legalTitles = {
      "/legal/privacy": "Privacy Policy | Orrith",
      "/legal/terms": "Terms of Service | Orrith",
      "/legal/security": "Security Policy | Orrith",
      "/legal/community-guidelines": "Community Guidelines | Orrith",
      "/legal/cookies": "Cookies Policy | Orrith",
      "/legal/dpa": "Data Processing Addendum | Orrith",
      "/legal/refund": "Refund Policy | Orrith",
    };

    return {
      title: legalTitles[path] || "Legal | Orrith",
      description:
        "Read Orrith's legal, privacy, and policy documentation for platform use and governance.",
      canonical: `${SITE_ORIGIN}${path}`,
      robots: "noindex, follow",
    };
  }

  const noindexPrefixes = [
    "/admin",
    "/login",
    "/signup",
    "/forgot-password",
    "/verify-email-action",
    "/search",
    "/me",
    "/settings",
    "/sellnow",
    "/inbox",
    "/messages",
    "/conversations",
    "/notifications",
    "/wishlist",
    "/edit-profile",
    "/edit-product",
    "/my-offers",
    "/offers",
    "/my-products",
    "/profile",
  ];

  const aliasRoutes = {
    "/offers": `${SITE_ORIGIN}/my-offers`,
    "/my-impact": `${SITE_ORIGIN}/farmmark`,
    "/privacy": `${SITE_ORIGIN}/legal/privacy`,
    "/terms": `${SITE_ORIGIN}/legal/terms`,
  };

  if (aliasRoutes[path]) {
    return {
      title: "Orrith",
      description:
        "Orrith is an agricultural intelligence infrastructure designed to coordinate food systems through market networks, supply-chain intelligence, environmental data, and measurable planetary impact",
      canonical: aliasRoutes[path],
      robots: "noindex, nofollow, noarchive",
    };
  }

  if (noindexPrefixes.some((prefix) => path === prefix || path.startsWith(`${prefix}/`))) {
    return {
      title: "Orrith",
      description:
        "Orrith is an agricultural intelligence infrastructure designed to coordinate food systems through market networks, supply-chain intelligence, environmental data, and measurable planetary impact",
      canonical: `${SITE_ORIGIN}${path}`,
      robots: "noindex, nofollow, noarchive",
    };
  }

  // Product detail pages get generic metadata — individual pages should override
  if (path.startsWith("/product/")) {
    return {
      title: "Product Details | Orrith",
      description:
        "View agricultural product listings, pricing, and seller information on the Orrith marketplace.",
      canonical: `${SITE_ORIGIN}${path}`,
      ogImage: OG_IMAGE,
    };
  }

  return {
    title: "Orrith | Cycle of Certainty | Agricultural Intelligence & Planetary Infrastructure",
    description:
      "Orrith is an agricultural intelligence infrastructure designed to coordinate food systems through market networks, supply-chain intelligence, environmental data, and measurable planetary impact",
    canonical: `${SITE_ORIGIN}${path}`,
    ogImage: OG_IMAGE,
  };
}

function applyRouteSeo(config) {
  if (typeof document === "undefined") return;

  const robots = config.robots || "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1";
  const canonical = config.canonical || `${SITE_ORIGIN}`;
  const title = config.title || "Orrith | Operating Infrastructure for Global Agriculture, Food Systems & Climate Intelligence";
  const description = config.description || "Orrith is building the operating infrastructure for global agriculture — connecting farmers, markets, food logistics, and climate intelligence through trusted networks that improve food access, reduce waste, and create measurable environmental impact.";
  const ogImage = config.ogImage || OG_IMAGE;
  const ogType = config.ogType || "website";
  const siteName = config.siteName || "Orrith";
  const locale = config.locale || "en_US";

  document.title = title;

  setHeadMeta('meta[name="title"]', { name: "title", content: title });
  setHeadMeta('meta[name="description"]', { name: "description", content: description });
  setHeadMeta('meta[name="robots"]', { name: "robots", content: robots });
  setHeadMeta('meta[name="googlebot"]', { name: "googlebot", content: robots });

  // Open Graph
  setHeadMeta('meta[property="og:type"]', { property: "og:type", content: ogType });
  setHeadMeta('meta[property="og:title"]', { property: "og:title", content: title });
  setHeadMeta('meta[property="og:description"]', { property: "og:description", content: description });
  setHeadMeta('meta[property="og:url"]', { property: "og:url", content: canonical });
  setHeadMeta('meta[property="og:image"]', { property: "og:image", content: ogImage });
  setHeadMeta('meta[property="og:site_name"]', { property: "og:site_name", content: siteName });
  setHeadMeta('meta[property="og:locale"]', { property: "og:locale", content: locale });

  // Twitter Card
  setHeadMeta('meta[property="twitter:card"]', { property: "twitter:card", content: "summary_large_image" });
  setHeadMeta('meta[property="twitter:site"]', { property: "twitter:site", content: TWITTER_HANDLE });
  setHeadMeta('meta[property="twitter:title"]', { property: "twitter:title", content: title });
  setHeadMeta('meta[property="twitter:description"]', { property: "twitter:description", content: description });
  setHeadMeta('meta[property="twitter:image"]', { property: "twitter:image", content: ogImage });

  setLinkTag("canonical", canonical);
}

export { SITE_ORIGIN, normalizePathname, getRouteSeo, applyRouteSeo };
